import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { Plus, RefreshCw, Key, Filter, Users as UsersIcon, UserCheck, Clock } from 'lucide-react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import UserTable from '@/components/admin/users/UserTable';
import ModernUserTable from '@/components/admin/users/ModernUserTable';
import PendingUsersTable from '@/components/admin/users/PendingUsersTable';
import UserFormDialog from '@/components/admin/users/UserFormDialog';
import UserFilters from '@/components/admin/users/UserFilters';
import UserStats from '@/components/admin/users/UserStats';
import Divider from '@/components/ui/divider';
import Skeleton from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/use-toast';
import { adminAPI } from '@/services/api';
import { adminSidebarLinks } from '@/config/adminSidebarLinks';
import usePendingUsers from '@/hooks/usePendingUsers';
import { useNavigate } from 'react-router-dom';

function AdminUsers() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isStatsLoading, setIsStatsLoading] = useState(false);
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [filters, setFilters] = useState({
    search: '',
    role: '',
    status: '',
    is_active: '',
  });

  // Hook global pour gérer le compteur d'utilisateurs en attente
  const { fetchPendingCount } = usePendingUsers();

  // Fetch users
  const fetchUsers = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = {};
      if (filters.search) params.search = filters.search;
      if (filters.role) params.role = filters.role;
      if (filters.status) params.status = filters.status;
      if (filters.is_active) params.is_active = filters.is_active;

      const response = await adminAPI.getUsers(params);
      const usersData = response.data.results || response.data;
      setUsers(usersData);
    } catch (error) {
      toast({
        title: 'Erreur',
        description: 'Impossible de charger les utilisateurs',
        variant: 'destructive',
      });
      console.error('Error fetching users:', error);
    } finally {
      setIsLoading(false);
    }
  }, [filters]);

  // Fetch statistics
  const fetchStatistics = async () => {
    setIsStatsLoading(true);
    try {
      const response = await adminAPI.getUserStatistics();
      setStatistics(response.data);
    } catch (error) {
      console.error('Error fetching statistics:', error);
    } finally {
      setIsStatsLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    fetchStatistics();
  }, []);

  // Filtrer les utilisateurs selon l'onglet actif
  const getFilteredUsers = () => {
    if (activeTab === 'pending') {
      return users.filter(user => user.status === 'PENDING');
    }
    return users;
  };

  // Compter les utilisateurs en attente
  const pendingCount = users.filter(user => user.status === 'PENDING').length;

  // Create or Update user
  const handleSubmitUser = async (userData) => {
    setIsLoading(true);
    try {
      if (selectedUser) {
        await adminAPI.updateUser(selectedUser.id, userData);
        toast({
          title: 'Succès',
          description: 'Utilisateur mis à jour avec succès',
        });
      } else {
        await adminAPI.createUser(userData);
        toast({
          title: 'Succès',
          description: 'Utilisateur créé avec succès',
        });
      }
      setFormDialogOpen(false);
      setSelectedUser(null);
      fetchUsers();
      fetchStatistics();
      fetchPendingCount(); // Mettre à jour le compteur global
    } catch (error) {
      const data = error.response?.data;
      let description = data?.message || data?.detail;
      if (!description && data && typeof data === 'object') {
        const parts = Object.entries(data).map(([k, v]) => {
          const msg = Array.isArray(v) ? v.map((x) => String(x)).join(', ') : String(v);
          return `${k}: ${msg}`;
        });
        description = parts.length ? parts.join(' · ') : 'Une erreur est survenue';
      }
      toast({
        title: 'Erreur',
        description: description || 'Une erreur est survenue',
        variant: 'destructive',
      });
      console.error('Error submitting user:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Delete user
  const handleDeleteUser = (user) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedUser) return;

    try {
      await adminAPI.deleteUser(selectedUser.id);
      toast({
        title: 'Succès',
        description: 'Utilisateur supprimé avec succès',
      });
      fetchUsers();
      fetchStatistics();
      fetchPendingCount(); // Mettre à jour le compteur global
      setDeleteDialogOpen(false);
      setSelectedUser(null);
    } catch (error) {
      // Extraire le message détaillé de l'erreur
      let errorMessage = 'Impossible de supprimer l\'utilisateur';

      if (error.response?.data) {
        // Priorité 1: message détaillé explicite
        if (error.response.data.detail) {
          errorMessage = error.response.data.detail;
        }
        // Priorité 2: message court
        else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        }
        // Priorité 3: message par défaut si c'est un objet
        else if (typeof error.response.data === 'object') {
          // Si c'est un ValidationError avec des détails
          const errorData = error.response.data;
          if (errorData.detail || errorData.message) {
            errorMessage = errorData.detail || errorData.message;
          }
        }
        // Priorité 4: message string direct
        else if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        }
      }

      toast({
        title: 'Erreur',
        description: errorMessage,
        variant: 'destructive',
      });
      console.error('Error deleting user:', error);
    }
  };

  // Activate user
  const handleActivateUser = async (userOrId) => {
    try {
      const userId = typeof userOrId === 'string' ? userOrId : userOrId.id;
      await adminAPI.activateUser(userId);
      toast({
        title: 'Succès',
        description: 'Utilisateur activé avec succès',
      });
      fetchUsers();
      fetchStatistics();
      fetchPendingCount(); // Mettre à jour le compteur global
    } catch (error) {
      toast({
        title: 'Erreur',
        description: 'Impossible d\'activer l\'utilisateur',
        variant: 'destructive',
      });
      console.error('Error activating user:', error);
    }
  };

  // Deactivate user
  const handleDeactivateUser = async (userOrId) => {
    try {
      const userId = typeof userOrId === 'string' ? userOrId : userOrId.id;
      await adminAPI.deactivateUser(userId);
      toast({
        title: 'Succès',
        description: 'Utilisateur désactivé avec succès',
      });
      fetchUsers();
      fetchStatistics();
      fetchPendingCount(); // Mettre à jour le compteur global
    } catch (error) {
      toast({
        title: 'Erreur',
        description: 'Impossible de désactiver l\'utilisateur',
        variant: 'destructive',
      });
      console.error('Error deactivating user:', error);
    }
  };

  // Reset password
  const handleResetPasswordClick = (user) => {
    setSelectedUser(user);
    setNewPassword('');
    setPasswordDialogOpen(true);
  };

  const handleResetPasswordSubmit = async () => {
    if (!newPassword || newPassword.length < 8) {
      toast({
        title: 'Erreur',
        description: 'Le mot de passe doit contenir au moins 8 caractères',
        variant: 'destructive',
      });
      return;
    }

    try {
      await adminAPI.resetPassword(selectedUser.id, newPassword);
      toast({
        title: 'Succès',
        description: 'Mot de passe réinitialisé avec succès',
      });
      setPasswordDialogOpen(false);
      setSelectedUser(null);
      setNewPassword('');
    } catch (error) {
    toast({
        title: 'Erreur',
        description: 'Impossible de réinitialiser le mot de passe',
        variant: 'destructive',
      });
      console.error('Error resetting password:', error);
    }
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setFormDialogOpen(true);
  };

  const handleCreateUser = () => {
    setSelectedUser(null);
    setFormDialogOpen(true);
  };

  const handleViewProfile = (user) => {
    navigate(`/admin/user/${user.id}`);
  };

  const handleResetFilters = () => {
    setFilters({
      search: '',
      role: '',
      status: '',
      is_active: '',
    });
  };

  // Actions pour les utilisateurs en attente
  const handleApproveUser = async (userId) => {
    try {
      await adminAPI.activateUser(userId);
      toast({
        title: 'Succès',
        description: 'Utilisateur validé avec succès',
      });
      fetchUsers();
      fetchStatistics();
      fetchPendingCount();
    } catch (error) {
      toast({
        title: 'Erreur',
        description: 'Impossible de valider l\'utilisateur',
        variant: 'destructive',
      });
      console.error('Error approving user:', error);
    }
  };

  const handleRejectUser = async (userId) => {
    try {
      await adminAPI.deactivateUser(userId);
      toast({
        title: 'Succès',
        description: 'Utilisateur rejeté avec succès',
      });
      fetchUsers();
      fetchStatistics();
      fetchPendingCount();
    } catch (error) {
      toast({
        title: 'Erreur',
        description: 'Impossible de rejeter l\'utilisateur',
        variant: 'destructive',
      });
      console.error('Error rejecting user:', error);
    }
  };

  const handleViewUserDetails = (user) => {
    setSelectedUser(user);
    setFormDialogOpen(true);
  };

  const filteredUsers = getFilteredUsers();

  return (
    <>
      <Helmet>
        <title>Gestion des Utilisateurs - LogiConnect</title>
      </Helmet>
      <DashboardLayout sidebarLinks={adminSidebarLinks}>
        <div className="space-y-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground truncate">
                Gestion des utilisateurs
              </h1>
              <p className="text-sm text-muted-foreground mt-1 hidden sm:block">
                Gérez les utilisateurs de la plateforme
              </p>
            </div>
            <div className="flex gap-2 sm:gap-3 flex-shrink-0">
              <Button
                variant="outline"
                onClick={fetchUsers}
                disabled={isLoading}
                size="default"
                className="sm:px-4"
              >
                <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''} sm:mr-2`} />
                <span className="hidden sm:inline">Actualiser</span>
              </Button>
              <Button
                onClick={handleCreateUser}
                size="default"
                className="sm:px-4"
              >
                <Plus className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Nouvel utilisateur</span>
          </Button>
            </div>
          </div>

          {/* Statistics */}
          {isStatsLoading ? (
            <div className="lg:grid lg:grid-cols-4 lg:gap-6">
              <div className="flex space-x-6 overflow-x-auto pb-4 -mx-4 px-4 lg:contents">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="w-64 flex-shrink-0 lg:w-auto">
                    <div className="bg-card border border-border rounded-xl p-6">
                      <Skeleton className="h-4 w-1/2 mb-3" />
                      <Skeleton className="h-8 w-3/4 mb-2" />
                      <Skeleton className="h-4 w-24" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <UserStats statistics={statistics} isLoading={false} />
          )}

          {/* Tabs Section */}
          <div className="bg-card rounded-lg border border-border p-4">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="w-full sm:w-auto">
                <TabsTrigger value="all" className="flex-1 sm:flex-none">
                  <UsersIcon className="h-4 w-4 mr-2" />
                  Tous les utilisateurs
                  <span className="ml-2 text-xs text-muted-foreground">({users.length})</span>
                </TabsTrigger>
                <TabsTrigger value="pending" badge={pendingCount} className="flex-1 sm:flex-none">
                  <Clock className="h-4 w-4 mr-2" />
                  À valider
                </TabsTrigger>
              </TabsList>

              <TabsContent value="all" className="space-y-6 mt-6">
                {/* Divider: Stats -> Filters */}
                <Divider variant="gradient" icon={Filter} label="Filtres & Recherche" />

                {/* Filters */}
                <UserFilters
                  filters={filters}
                  onFiltersChange={setFilters}
                  onReset={handleResetFilters}
                />

                {/* Divider: Filters -> Table */}
                <Divider variant="default" icon={UsersIcon} label="Liste des Utilisateurs" />

                {/* Users Table */}
                {isLoading ? (
                  <div className="bg-card border border-border rounded-xl p-4">
                    {[...Array(6)].map((_, i) => (
                      <div key={i} className="grid grid-cols-6 gap-4 py-3 border-b border-border last:border-0">
                        <Skeleton className="h-4 w-24 col-span-2" />
                        <Skeleton className="h-4 w-24" />
                        <Skeleton className="h-4 w-16" />
                        <Skeleton className="h-4 w-20" />
                        <Skeleton className="h-8 w-20 justify-self-end" />
                      </div>
                    ))}
                  </div>
                ) : (
                  <ModernUserTable
                    users={filteredUsers}
                    onEdit={handleEditUser}
                    onDelete={handleDeleteUser}
                    onActivate={handleActivateUser}
                    onDeactivate={handleDeactivateUser}
                    onResetPassword={handleResetPasswordClick}
                    onViewProfile={handleViewProfile}
                    isLoading={false}
                  />
                )}
              </TabsContent>

              <TabsContent value="pending" className="space-y-6 mt-6">
                {/* Message si aucun utilisateur en attente */}
                {pendingCount === 0 ? (
                  <div className="text-center py-12 bg-card rounded-lg border border-border">
                    <div className="flex flex-col items-center justify-center gap-3">
                      <div className="h-16 w-16 rounded-full bg-green-500/10 flex items-center justify-center">
                        <UserCheck className="h-8 w-8 text-green-500" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-foreground mb-1">
                          Tout est à jour !
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          Aucun utilisateur en attente de validation
                        </p>
                      </div>
                    </div>
                        </div>
                ) : (
                  <>
                    <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4">
                      <div className="flex items-center gap-3">
                        <Clock className="h-5 w-5 text-yellow-500 flex-shrink-0" />
                        <div>
                          <p className="text-sm font-medium text-foreground">
                            {pendingCount} utilisateur{pendingCount > 1 ? 's' : ''} en attente de validation
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            Ces utilisateurs nécessitent votre attention pour être activés
                          </p>
                        </div>
                      </div>
                    </div>

                    <Divider variant="default" icon={Clock} label="Utilisateurs en attente" />

                    {/* Table des utilisateurs en attente */}
                    {isLoading ? (
                      <div className="bg-card border border-border rounded-xl p-4">
                        {[...Array(3)].map((_, i) => (
                          <div key={i} className="grid grid-cols-6 gap-4 py-3 border-b border-border last:border-0">
                            <Skeleton className="h-4 w-24 col-span-2" />
                            <Skeleton className="h-4 w-24" />
                            <Skeleton className="h-4 w-16" />
                            <Skeleton className="h-4 w-20" />
                            <Skeleton className="h-8 w-20 justify-self-end" />
                          </div>
                        ))}
                      </div>
                    ) : (
                      <PendingUsersTable
                        users={filteredUsers}
                        onApprove={handleApproveUser}
                        onReject={handleRejectUser}
                        onViewDetails={handleViewUserDetails}
                        onDelete={handleDeleteUser}
                        isLoading={false}
                      />
                    )}
                  </>
                )}
              </TabsContent>
            </Tabs>
          </div>

          {/* Form Dialog */}
          <UserFormDialog
            open={formDialogOpen}
            onClose={() => {
              setFormDialogOpen(false);
              setSelectedUser(null);
            }}
            onSubmit={handleSubmitUser}
            user={selectedUser}
            isLoading={isLoading}
          />

          {/* Password Reset Dialog */}
          <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
            <DialogContent>
                <DialogHeader>
                <DialogTitle>Réinitialiser le mot de passe</DialogTitle>
                <DialogDescription>
                  Définir un nouveau mot de passe pour{' '}
                  <span className="font-semibold">{selectedUser?.email}</span>
                    </DialogDescription>
                </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="new-password">Nouveau mot de passe</Label>
                  <Input
                    id="new-password"
                    type="password"
                    placeholder="Au moins 8 caractères"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Le mot de passe doit contenir au moins 8 caractères
                  </p>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setPasswordDialogOpen(false)}>
                  Annuler
                </Button>
                <Button onClick={handleResetPasswordSubmit}>
                  <Key className="h-4 w-4 mr-2" />
                  Réinitialiser
                </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
        </div>

        {/* Modale de suppression */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Supprimer l'utilisateur</AlertDialogTitle>
              <AlertDialogDescription>
                Êtes-vous sûr de vouloir supprimer l'utilisateur <strong>{selectedUser?.first_name} {selectedUser?.last_name}</strong> ?
                Cette action est irréversible et supprimera toutes les données associées à cet utilisateur.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annuler</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleConfirmDelete}
                className="bg-red-600 hover:bg-red-700"
              >
                Supprimer
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DashboardLayout>
    </>
  );
}

export default AdminUsers;
